import React, { forwardRef } from 'react';
import { purchaseOrder } from '../../model/purchase-order';
import { convertFormatDateTime } from '../../utils/util';
import { printDocument } from '../../utils/print-utils';

interface Props {
    data: purchaseOrder;
    onClose?: () => void;
}

export const printPurchaseOrderDocument = (contentElement: HTMLElement | null) => {
    printDocument(contentElement, {
        documentType: 'a4-portrait',
        title: `ใบสั่งซื้อสินค้า ${contentElement?.querySelector('.po-print-doc-no')?.textContent || ''}`
    });
};

const PurchaseOrderPrint = forwardRef<HTMLDivElement, Props>(({ data }, ref) => {
    const subtotal = data.detail?.reduce((sum, item) => sum + (Number(item.total_cost) || (Number(item.quantity) * Number(item.cost))), 0) || 0;
    const vatRate = data.vat === 0 || data.vat === 1 ? 0.07 : 0;
    const vatAmount = data.vat === 1 ? subtotal * vatRate : data.vat === 0 ? subtotal - (subtotal / 1.07) : 0;
    const grandTotal = data.vat === 1 ? subtotal + vatAmount : subtotal;

    return (
        <div 
            ref={ref}
            className="po-print-container" 
            data-testid="po-print-document"
            style={{ 
                padding: '24px', 
                background: '#FFFFFF', 
                color: '#1E293B', 
                fontFamily: 'Kodchasan, Prompt, sans-serif' 
            }}
        >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #FF7A59', paddingBottom: '16px', marginBottom: '20px' }}>
                <div>
                    <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#FF7A59', margin: 0 }}>🥕 JinDa POS Store</h1>
                    <p style={{ fontSize: '12px', color: '#64748B', margin: '4px 0 0 0' }}>123/45 ถนนสุขุมวิท กรุงเทพมหานคร 10110 | โทร: 02-123-4567 | เลขผู้เสียภาษี: 0105559999999</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1E293B', margin: 0 }}>ใบสั่งซื้อสินค้า (PURCHASE ORDER)</h2>
                    <p className="po-print-doc-no" style={{ fontSize: '13px', fontWeight: 600, color: '#FF7A59', margin: '4px 0 0 0' }}>เลขที่: {data.document_no || 'PO-DRAFT'}</p>
                    <p style={{ fontSize: '12px', color: '#64748B', margin: 0 }}>วันที่: {typeof data.create_date === 'string' ? data.create_date : (data.create_date ? convertFormatDateTime(data.create_date) : new Date().toLocaleDateString('th-TH'))}</p>
                </div>
            </div>

            {/* Vendor & Delivery Info */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', background: '#F8FAFC', padding: '14px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #E2E8F0', fontSize: '13px' }}>
                <div>
                    <strong style={{ color: '#FF7A59' }}>ผู้จัดจำหน่าย (Vendor):</strong>
                    <div style={{ fontWeight: 600, marginTop: '2px' }}>{data.vendor_name || 'ไม่ได้ระบุ'}</div>
                    <div style={{ color: '#64748B' }}>ที่อยู่: {data.vendor_address || '-'}</div>
                    <div style={{ color: '#64748B' }}>โทร: {data.vendor_tel || '-'}</div>
                </div>
                <div>
                    <strong style={{ color: '#FF7A59' }}>ข้อมูลการจัดส่ง / หมายเหตุ:</strong>
                    <div style={{ color: '#334155', marginTop: '2px' }}>ส่งที่: สาขาหลัก JinDa POS Store</div>
                    <div style={{ color: '#64748B' }}>หมายเหตุ: {data.remark || '-'}</div>
                </div>
            </div>

            {/* Items Table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px', fontSize: '13px' }}>
                <thead>
                    <tr style={{ background: '#FFF3EE', borderBottom: '2px solid #FFCBBB', color: '#FF7A59' }}>
                        <th style={{ padding: '8px 10px', textAlign: 'center', width: '50px' }}>ลำดับ</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left', width: '130px' }}>รหัสสินค้า</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left' }}>รายการสินค้า</th>
                        <th style={{ padding: '8px 10px', textAlign: 'center', width: '90px' }}>จำนวน</th>
                        <th style={{ padding: '8px 10px', textAlign: 'right', width: '110px' }}>ต้นทุน/หน่วย</th>
                        <th style={{ padding: '8px 10px', textAlign: 'right', width: '120px' }}>รวม (บาท)</th>
                    </tr>
                </thead>
                <tbody>
                    {data.detail && data.detail.length > 0 ? (
                        data.detail.map((item, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #E2E8F0', pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                                <td style={{ padding: '8px 10px', textAlign: 'center' }}>{idx + 1}</td>
                                <td style={{ padding: '8px 10px', fontWeight: 600 }}>{item.product_code}</td>
                                <td style={{ padding: '8px 10px' }}>{item.product_name}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700 }}>{item.quantity}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right' }}>{Number(item.cost || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>{Number(item.total_cost || (item.quantity * item.cost) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={6} style={{ textAlign: 'center', padding: '20px', color: '#94A3B8' }}>ไม่มีรายการสินค้า</td>
                        </tr>
                    )}
                </tbody>
            </table>

            {/* Totals Summary */}
            <div className="po-totals-section" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '36px', pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                <div style={{ width: '280px', fontSize: '13px', lineHeight: '1.8' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748B' }}>
                        <span>ยอดรวมก่อนภาษี:</span>
                        <span>{subtotal.toLocaleString('en-US', { minimumFractionDigits: 2 })} ฿</span>
                    </div>
                    {data.vat !== -1 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748B' }}>
                            <span>ภาษีมูลค่าเพิ่ม (VAT 7%):</span>
                            <span>{vatAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })} ฿</span>
                        </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: 700, color: '#FF7A59', borderTop: '2px solid #E2E8F0', paddingTop: '6px', marginTop: '4px' }}>
                        <span>ยอดเงินสุทธิ:</span>
                        <span>{grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })} ฿</span>
                    </div>
                </div>
            </div>

            {/* Signatures */}
            <div className="po-signatures-section" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', textAlign: 'center', fontSize: '12px', color: '#64748B', paddingTop: '20px', borderTop: '1px dashed #CBD5E1', pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                <div>
                    <div style={{ height: '50px' }}></div>
                    <div style={{ borderBottom: '1px solid #94A3B8', width: '80%', margin: '0 auto 6px' }}></div>
                    <div>( ผู้จัดทำเอกสาร )</div>
                    <div>วันที่: ___/___/______</div>
                </div>
                <div>
                    <div style={{ height: '50px' }}></div>
                    <div style={{ borderBottom: '1px solid #94A3B8', width: '80%', margin: '0 auto 6px' }}></div>
                    <div>( ผู้จัดการ / ผู้อนุมัติ )</div>
                    <div>วันที่: ___/___/______</div>
                </div>
                <div>
                    <div style={{ height: '50px' }}></div>
                    <div style={{ borderBottom: '1px solid #94A3B8', width: '80%', margin: '0 auto 6px' }}></div>
                    <div>( ผู้รับใบสั่งซื้อ / Vendor )</div>
                    <div>วันที่: ___/___/______</div>
                </div>
            </div>
        </div>
    );
});

PurchaseOrderPrint.displayName = 'PurchaseOrderPrint';

export default PurchaseOrderPrint;
